// Scanner-event chart markers — shared by the classic StockChart and the Pro
// Terminal. Turns the volume-spike scanner's qualified hits (ScannerEvent[])
// into lightweight-charts dot markers, color-coded by qualification reason.
//
// Kept dependency-free (only TYPE imports) so it lives alongside the rest of
// `lib/chart` and can be unit-tested in isolation — see scannerMarkers.test.ts.
// The trickiest piece is `scannerIsoToChartTime`: scanner events arrive as TRUE
// UTC ISO timestamps, but intraday chart bars are ET wall-clock stamped AS-IF
// UTC (so a 09:30 ET bar reads "09:30" on the UTC-formatted axis). To land a
// dot on the right bar we extract the event's ET wall-clock via Intl and
// re-encode it as UTC. Both StockChart (toTime) and the terminal
// (timeframe.ts ohlcvTimeToEpochSec) use this same as-if-UTC convention.

import type { SeriesMarker, Time } from 'lightweight-charts';
import type { ScannerEvent } from '@/lib/api-client';

/** Hex palette for confirmation dots, keyed on qualifyReason. Mirrors the
 *  Tailwind pills used on the Scanner Log page so the same reason reads the
 *  same color across surfaces. */
export const REASON_COLOR: Record<string, string> = {
  projected_rvol: '#3b82f6',      // blue-500
  recent_rvol:    '#06b6d4',      // cyan-500
  recent_rvol_confirmed: '#22c55e', // green-500 (confirmed / new-high)
  velocity:       '#a855f7',      // purple-500
  pressure:       '#ec4899',      // pink-500
  gap_and_go:     '#10b981',      // emerald-500
  gap_and_go_confirmed: '#22c55e', // green-500 (confirmed / new-high)
  orb_breakout:   '#f59e0b',      // amber-500
  trend_continuation: '#14b8a6',  // teal-500
  confluence_stack: '#facc15',    // yellow-400 (re-added plan #242)
  // Plan #234 — live BUY/SELL signal transitions: a VWAP-aligned 30-min
  // breakout on volume, or (2026-09-10) a confirmed session-low reversal
  // candle. Sky for buy, lighter rose for sell.
  live_signal_buy:  '#38bdf8',    // sky-400
  live_signal_sell: '#fda4af',    // rose-300
  // Plan #359 — Eixom (HTF MACD-histogram) crosses ≤0 → positive and rising.
  // Lime: a momentum "go" distinct from the greens/sky already in use.
  eixom_cross_up:   '#84cc16',    // lime-500
  // opening_drive — driving up off the 09:30 opening print on heavy opening
  // volume. Indigo: reads as its own thing next to the burst blues/cyans,
  // which matters because it lands in the same 15 minutes as gap_and_go.
  opening_drive:    '#818cf8',    // indigo-400
  // momentum_run + steady_climber are RETIRED — the scanner emits neither.
  // The entries stay so 7-day-retained historical dots still render in the
  // color they were logged under instead of falling back to the default.
  momentum_run:     '#fb923c',    // orange-400
  // fast_dip — bearish 5-min flush (>$10 names). Red: the only DROP signal in
  // the set, so it reads unmistakably as down/sell-side vs the bullish hues.
  fast_dip:         '#ef4444',    // red-500
};

export const REASON_LABEL: Record<string, string> = {
  projected_rvol: 'Proj RVOL',
  recent_rvol:    'Recent RVOL',
  recent_rvol_confirmed: 'Recent RVOL ✓',
  velocity:       'Velocity',
  pressure:       'Pressure',
  gap_and_go:     'Gap & Go',
  gap_and_go_confirmed: 'Gap & Go ✓',
  orb_breakout:   'ORB',
  trend_continuation: 'Trend Cont.',
  confluence_stack: 'Confluence',
  live_signal_buy:  'Live BUY',
  live_signal_sell: 'Live SELL',
  eixom_cross_up:   'Eixom ↑',
  opening_drive:    'Opening Drive',
  momentum_run:     'Momentum Run',   // retired 2026-08-31, historical rows only
  fast_dip:         'Fast Dip',
};

/** Reasons the scanner may still emit / retain but that are HIDDEN from every
 *  chart dot layer, reason filter and legend (Dan, 2026-09-10: "remove
 *  pressure and confluence from the visible filter and the dots; leave the
 *  logic in"). The backend, the auto-trade opt-in lists and the label/color
 *  maps above are untouched so historical rows and admin config still read
 *  correctly. To un-hide a reason, delete it here — nothing else. */
export const HIDDEN_SCANNER_REASONS: ReadonlySet<string> = new Set(['pressure', 'confluence_stack']);

export function isHiddenScannerReason(reason: string): boolean {
  return HIDDEN_SCANNER_REASONS.has(reason);
}

/** Canonical ordered reason list (drives "all enabled" defaults + filter UIs).
 *  Hidden reasons are excluded so they never appear as chips or defaults. */
export const SCANNER_REASONS = Object.keys(REASON_COLOR).filter((r) => !isHiddenScannerReason(r));

/** Gray fallback for a reason with no palette entry (matches the AI-signal dot). */
export const SCANNER_FALLBACK_COLOR = '#94a3b8';

/** Marker `size` for a scanner dot (one per chart bar). */
export const SCANNER_DOT_SIZE = 1;

/** Shape for a scanner event marker. Default 'circle'; a live BUY/SELL that
 *  AI-validated (ev.aiValidation === 'approved') upgrades to a triangle
 *  — lightweight-charts' arrowUp/arrowDown — pointing in the signal direction.
 *  Any other validation state (pending / rejected / timeout / error / absent)
 *  stays a circle: the triangle is strictly the "AI stands behind this one"
 *  badge. Color is unchanged either way (REASON_COLOR still applies). */
export function scannerMarkerShape(ev: Pick<ScannerEvent, 'reason' | 'aiValidation'>): 'circle' | 'arrowUp' | 'arrowDown' {
  if (ev.aiValidation !== 'approved') return 'circle';
  if (ev.reason === 'live_signal_buy') return 'arrowUp';
  if (ev.reason === 'live_signal_sell') return 'arrowDown';
  return 'circle';
}
/** Fill for a bar carrying 2+ distinct enabled scanner reasons — a single white
 *  dot meaning "multiple signals here, filter a reason to disambiguate". A bar
 *  with exactly one enabled reason shows that reason's color instead. Collapsing
 *  a multi-reason bar to one white dot keeps exactly one marker per bar, so
 *  concentric `inBar` circles never stack/occlude and toggling a reason can't
 *  change whether a neighbouring dot is visible. */
export const SCANNER_MULTI_COLOR = '#ffffff';

/** ISO timestamp (UTC) → ET date as YYYY-MM-DD. Used to gate scanner markers by
 *  the chart's displayed day so out-of-range markers don't get pinned to the
 *  nearest bar by lightweight-charts. */
export function scannerIsoToETDate(iso: string): string | null {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const yyyy = get('year'), mm = get('month'), dd = get('day');
  if (!yyyy || !mm || !dd) return null;
  return `${yyyy}-${mm}-${dd}`;
}

/** ISO timestamp (UTC) → chart's wall-clock-as-UTC seconds. The chart treats
 *  bar dates like "2026-05-04 14:32" as UTC even though they're actually ET;
 *  scanner events arrive as true UTC ISO, so we extract the ET wall-clock and
 *  re-encode as UTC to land on the same axis. */
export function scannerIsoToChartTime(iso: string): number | null {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const yyyy = get('year'), mm = get('month'), dd = get('day');
  let hh = get('hour'); if (hh === '24') hh = '00';
  const mi = get('minute'), ss = get('second');
  const ms = new Date(`${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}Z`).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

/** Collapse timestamped signal runs to ONE entry per chart bar.
 *
 *  AI chart-signal / auto-trader runs are evaluated on their OWN cadence (every
 *  1 or 5 minutes, plus a server 5-min series), independent of the bar interval
 *  the user is viewing. When that cadence is FINER than the on-screen bar (e.g.
 *  five 1-minute runs inside one 5-minute bar), every run maps to the same bar;
 *  lightweight-charts fans same-bar `aboveBar`/`belowBar` markers out
 *  vertically, so the arrows visibly stack. Collapsing to one marker per bar
 *  makes the marker cadence follow the CHART (the only cadence that can be
 *  rendered at one-marker-per-bar width) regardless of the run cadence.
 *
 *  Keeps the LATEST run per bar — the most recent decision as of that bar — and
 *  anchors it at the bar's START time (chartTimeSec floored to the bar grid),
 *  which aligns to real intraday bar opens (09:30, 09:35, … are all on the
 *  1/5/15-min grid). Handles both mismatch directions: a finer run cadence
 *  collapses to one-per-bar; a coarser one (e.g. 5-min runs on a 1-min chart)
 *  simply yields at most one marker per populated bar. Pure.
 *
 *  @param runs each carries a chart-time (seconds, as-if-UTC — from
 *    scannerIsoToChartTime or backtestBarTimeToChartTime), an `orderKey` used to
 *    pick the latest within a bar (requestedAt epoch ms, or array index for
 *    manual runs), and a signal. Null-signal runs are dropped.
 *  @param barSec on-screen bar interval in seconds (cadenceMin * 60). Falls back
 *    to 60 for a non-positive value so a bad interval can't divide-by-zero. */
export function collapseSignalRunsPerBar<S extends string>(
  runs: ReadonlyArray<{ chartTimeSec: number; orderKey: number; signal: S | null }>,
  barSec: number,
): { barTime: number; signal: S }[] {
  const step = barSec > 0 ? barSec : 60;
  const latestByBar = new Map<number, { orderKey: number; signal: S }>();
  for (const r of runs) {
    if (r.signal == null) continue;
    if (!Number.isFinite(r.chartTimeSec)) continue;
    const barTime = Math.floor(r.chartTimeSec / step) * step;
    const prev = latestByBar.get(barTime);
    if (!prev || r.orderKey > prev.orderKey) latestByBar.set(barTime, { orderKey: r.orderKey, signal: r.signal });
  }
  return [...latestByBar.entries()]
    .map(([barTime, v]) => ({ barTime, signal: v.signal }))
    .sort((a, b) => a.barTime - b.barTime);
}

/** Reasons that have ≥1 event on a date in the visible window. Drives which
 *  filter chips/legend entries appear so off-window reasons don't show. */
export function presentReasons(events: ScannerEvent[], visibleEtDates: ReadonlySet<string>): Set<string> {
  const present = new Set<string>();
  for (const ev of events) {
    if (isHiddenScannerReason(ev.reason)) continue;
    const evDateET = scannerIsoToETDate(ev.detectedAt);
    if (!evDateET || !visibleEtDates.has(evDateET)) continue;
    present.add(ev.reason);
  }
  return present;
}

export interface ScannerMarkerOpts {
  /** Only emit markers for these reasons. Omit = every reason in REASON_COLOR. */
  enabledReasons?: ReadonlySet<string>;
  /** Only emit markers whose ET date is in this set. Omit = no day gate. */
  visibleEtDates?: ReadonlySet<string>;
  /** Drop markers past this chart-time second (bar-replay cursor). Omit = none. */
  maxTimeSec?: number;
  /** On-screen bar interval in seconds (cadenceMin * 60). Events are bucketed
   *  to the bar that CONTAINS them and the marker is anchored at that bar's
   *  start — the same flooring `collapseSignalRunsPerBar` uses.
   *
   *  Required for same-bar grouping to work at all: scanner events carry
   *  second-precision timestamps, so a dozen firings inside one 5m bar have a
   *  dozen distinct chart times. Without a bar grid they can only be grouped by
   *  exact timestamp, which collapses nothing — lightweight-charts then snaps
   *  each to the same bar and fans them out into a tower of identical dots.
   *
   *  Flooring also fixes a quieter off-by-one-bar: lightweight-charts snaps a
   *  marker to the NEAREST bar, so an event at 09:33 on a 5m chart landed on
   *  09:35. It belongs to the 09:30 bar.
   *
   *  Omit (or pass a non-positive value) to group by exact timestamp — the
   *  pre-existing behaviour, kept as a safe default for callers with no bar
   *  grid (e.g. non-intraday charts). */
  barSec?: number;
  /** Which extended sessions the chart is actually RENDERING. Events in a
   *  hidden session are dropped.
   *
   *  Without this they leak: `visibleEtDates` gates by DATE, so a Sep-2
   *  after-hours event passes on a 2D chart showing Sep 2 + Sep 3 even when the
   *  AH toggle is off and no AH bar exists to carry it. lightweight-charts then
   *  snaps every such orphan to the NEAREST bar, and a whole session's worth
   *  piles onto one edge bar as a tower of dots climbing far above the price.
   *  SNOW carried 20 after-hours recent_rvol events on 2026-09-02 that stacked
   *  on the Sep-3 open exactly this way.
   *
   *  Session boundaries are read off the chart time directly — it encodes ET
   *  wall-clock as-if-UTC, so seconds-since-midnight IS the ET time of day and
   *  needs no further timezone conversion.
   *
   *  Omit to keep every session — the default, correct for callers rendering
   *  all sessions and for non-intraday charts where the concept doesn't apply. */
  visibleSessions?: { premarket: boolean; afterHours: boolean };
  /** Render unknown reasons in gray instead of dropping them. Default false. */
  includeUnknownReasons?: boolean;
  /** Layout. 'stacked' (candle / bar / hollow charts): one colored dot per
   *  (BAR, REASON), positioned `aboveBar` — lightweight-charts stacks same-bar
   *  aboveBar markers vertically, so multiple reasons on one bar never overlap,
   *  each in its own color above the candle. Repeat firings of the SAME reason
   *  within a bar collapse to one dot (they'd otherwise tower above the price).
   *  'aggregated' (line / area charts, default):
   *  one dot per BAR, `inBar` on the line — a single enabled reason shows its
   *  color, 2+ distinct reasons collapse to one white dot (concentric inBar
   *  circles would otherwise overlap on the line). */
  mode?: 'stacked' | 'aggregated';
}

interface QualifiedScannerHit { t: number; reason: string; color: string; baseId: string; shape: 'circle' | 'arrowUp' | 'arrowDown'; }

const RTH_OPEN_SEC = 9.5 * 3600;   // 09:30 ET
const RTH_CLOSE_SEC = 16 * 3600;   // 16:00 ET

/** Which ET session a chart time falls in. Chart time is ET wall-clock encoded
 *  as-if-UTC, so seconds-since-midnight is directly the ET time of day. */
export function etSessionOf(chartTimeSec: number): 'premarket' | 'regular' | 'afterHours' {
  const sod = ((chartTimeSec % 86400) + 86400) % 86400;
  if (sod < RTH_OPEN_SEC) return 'premarket';
  if (sod >= RTH_CLOSE_SEC) return 'afterHours';
  return 'regular';
}

/** Shared filter — one entry per event that passes the reason / date / replay
 *  gates, carrying its chart time, reason color, shape (AI-validated live
 *  signals become triangles), and stable id base. */
function qualifyScannerHits(events: ScannerEvent[], opts: ScannerMarkerOpts): QualifiedScannerHit[] {
  const { enabledReasons, visibleEtDates, maxTimeSec, visibleSessions, includeUnknownReasons = false } = opts;
  const hits: QualifiedScannerHit[] = [];
  for (const ev of events) {
    if (isHiddenScannerReason(ev.reason)) continue;
    if (enabledReasons && !enabledReasons.has(ev.reason)) continue;
    const color = REASON_COLOR[ev.reason] ?? (includeUnknownReasons ? SCANNER_FALLBACK_COLOR : undefined);
    if (!color) continue;
    if (visibleEtDates) {
      const evDateET = scannerIsoToETDate(ev.detectedAt);
      if (!evDateET || !visibleEtDates.has(evDateET)) continue;
    }
    const t = scannerIsoToChartTime(ev.detectedAt);
    if (t === null) continue;
    if (maxTimeSec != null && t > maxTimeSec) continue;
    if (visibleSessions) {
      const sess = etSessionOf(t);
      if (sess === 'premarket' && !visibleSessions.premarket) continue;
      if (sess === 'afterHours' && !visibleSessions.afterHours) continue;
    }
    hits.push({ t, reason: ev.reason, color, baseId: `scanner-${ev.detectedAt}-${ev.reason}`, shape: scannerMarkerShape(ev) });
  }
  return hits;
}

/** ScannerEvent[] → circle markers, sorted ascending by time. Pure. Hosts merge
 *  the result into their own marker array (alongside indicator/backtest markers)
 *  and call setMarkers; the markers plugin should be created with
 *  `{ autoScale: false }` so dots don't reshape the price axis.
 *
 *  Two layouts (opts.mode):
 *  - 'stacked' (candle / bar / hollow): one colored dot per (BAR, REASON),
 *    `aboveBar`. lightweight-charts stacks same-bar aboveBar markers vertically,
 *    so multiple reasons on one bar never overlap — each shows in its own color
 *    above the candle. Repeat firings of the SAME reason inside a bar collapse
 *    to a single dot, so a busy bar can't build a tower of identical dots. No
 *    white-collapse.
 *  - 'aggregated' (line / area, default): one dot per BAR, `inBar` on the line.
 *    A single enabled reason renders that reason's color; a bar with 2+ DISTINCT
 *    enabled reason-colors renders ONE white dot (SCANNER_MULTI_COLOR). One
 *    marker per bar means concentric inBar circles never overlap, so toggling a
 *    reason can't change a neighbour's visibility, and un-checking down to one
 *    reason reveals its true color. */
export function buildScannerMarkers(events: ScannerEvent[], opts: ScannerMarkerOpts = {}): SeriesMarker<Time>[] {
  const hits = qualifyScannerHits(events, opts);
  const out: SeriesMarker<Time>[] = [];
  // Bucket each event's second-precision chart time to the bar containing it.
  // Without a bar grid this is the identity, preserving the old behaviour.
  const step = opts.barSec != null && opts.barSec > 0 ? opts.barSec : 0;
  const barTimeOf = (t: number) => (step > 0 ? Math.floor(t / step) * step : t);
  if (opts.mode === 'stacked') {
    // One colored dot per (BAR, REASON) — not per event. lightweight-charts
    // stacks same-bar aboveBar markers vertically, which is what makes multiple
    // REASONS on one bar fan out legibly. But the scanner can re-fire the same
    // reason many times inside a single bar (a 5m bar routinely carries a dozen
    // recent_rvol hits), and one dot per event turned that into a tower of
    // identical dots climbing far above the price — visually broken, and it
    // buries the neighbouring bars' dots. Collapsing repeats keeps the fan-out
    // that was intended while capping the stack at the number of distinct
    // reasons actually present.
    //
    // Grouped by reason rather than color: two reasons could share a color, and
    // "same type" is the reason, not the swatch.
    const byBarReason = new Map<string, QualifiedScannerHit & { barTime: number; mixedShape: boolean }>();
    for (const h of hits) {
      const barTime = barTimeOf(h.t);
      const key = `${barTime}|${h.reason}`;
      const existing = byBarReason.get(key);
      if (!existing) {
        byBarReason.set(key, { ...h, barTime, mixedShape: false });
      } else if (existing.shape !== h.shape) {
        // Same reason on one bar with disagreeing shapes means one firing was
        // AI-approved and another wasn't. Degrade to the neutral circle rather
        // than let whichever arrived first imply a verdict for the group —
        // same convention the aggregated branch uses.
        existing.mixedShape = true;
      }
    }
    for (const [, h] of byBarReason) {
      out.push({
        time: h.barTime as Time,
        position: 'aboveBar',
        shape: h.mixedShape ? 'circle' : h.shape,
        color: h.color,
        text: '',
        size: SCANNER_DOT_SIZE,
        // Keyed on (bar, reason) so the id is stable no matter which of the
        // collapsed firings happened to be first in the input.
        id: `scanner-${h.barTime}-${h.reason}`,
      });
    }
  } else {
    // Group by bar; track distinct colors so a 2+-color bar collapses to white.
    // A bar keeps a triangle shape only while every hit on it agrees (in
    // practice: the single AI-validated live signal on that bar); any mix of
    // shapes or 2+ colors degrades to the aggregate circle.
    // Bucketed the same way as the stacked branch — "one dot per bar" was only
    // true when every event on a bar shared a timestamp to the second.
    const byBar = new Map<number, { colors: Set<string>; color: string; id: string; shape: QualifiedScannerHit['shape'] }>();
    for (const h of hits) {
      const barTime = barTimeOf(h.t);
      const existing = byBar.get(barTime);
      if (existing) {
        existing.colors.add(h.color);
        if (existing.shape !== h.shape) existing.shape = 'circle';
      } else {
        byBar.set(barTime, { colors: new Set([h.color]), color: h.color, id: h.baseId, shape: h.shape });
      }
    }
    for (const [t, bar] of byBar) {
      const multi = bar.colors.size >= 2;
      out.push({
        time: t as Time,
        position: 'inBar',
        shape: multi ? 'circle' : bar.shape,
        color: multi ? SCANNER_MULTI_COLOR : bar.color,
        text: '',
        size: SCANNER_DOT_SIZE,
        id: multi ? `scanner-${t}-multi` : bar.id,
      });
    }
  }
  out.sort((a, b) => (a.time as number) - (b.time as number));
  return out;
}
